import { httpsCallable } from 'firebase/functions';
import type {
  AgoraCredentials,
  CallActionResponse,
  CallIdRequest,
  CancelServiceRequestRequest,
  CreateCallRequest,
  CreateCallResponse,
  CreateServiceRequestRequest,
  CreateServiceRequestResponse,
  GetGuestServicesRequest,
  GetGuestServicesResponse,
  GetPlacesToExploreRequest,
  GetPlacesToExploreResponse,
  HealthResponse,
  RegisterGuestSessionRequest,
  RegisterGuestSessionResponse,
  RequestStatusResponse,
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
} from '@hotel-voice/shared';
import { firebaseFunctions } from './app';

/**
 * Typed wrappers around the callable Cloud Functions.
 *
 * Callables are used rather than HTTP endpoints so the Firebase SDK attaches
 * the ID token and the App Check token automatically — there is no REST API and
 * no bespoke auth header anywhere in this system.
 */
function callable<TRequest, TResponse>(name: string) {
  return async (payload: TRequest): Promise<TResponse> => {
    const fn = httpsCallable<TRequest, TResponse>(firebaseFunctions(), name);
    const result = await fn(payload);
    return result.data;
  };
}

export const health = callable<Record<string, never>, HealthResponse>('health');

export const registerGuestSession = callable<
  RegisterGuestSessionRequest,
  RegisterGuestSessionResponse
>('registerGuestSession');

export const createCall = callable<CreateCallRequest, CreateCallResponse>('createCall');

export const cancelCall = callable<CallIdRequest, CallActionResponse>('cancelCall');

export const endCall = callable<CallIdRequest, CallActionResponse>('endCall');

export const markCallConnecting = callable<CallIdRequest, CallActionResponse>(
  'markCallConnecting',
);

export const markCallConnected = callable<CallIdRequest, CallActionResponse>(
  'markCallConnected',
);

/** Token renewal — called on Agora's token-privilege-will-expire event. */
export const getAgoraCredentials = callable<CallIdRequest, AgoraCredentials>(
  'getAgoraCredentials',
);

/* -------------------------------------------------------------------------- */
/* Guest services, requests and feedback                                      */
/* -------------------------------------------------------------------------- */

/**
 * The catalogue — which services are on and what each offers — for a guest
 * with a live session. Served by a callable, not read from Firestore: the
 * hotel and settings documents are closed to guests by the rules.
 */
export const getGuestServices = callable<GetGuestServicesRequest, GetGuestServicesResponse>(
  'getGuestServices',
);

/**
 * Places to Explore — enabled places and tour packages. Its own callable
 * rather than part of `getGuestServices`, so the home page does not carry
 * every description and photo path just to decide whether to draw a tile.
 */
export const getPlacesToExplore = callable<GetPlacesToExploreRequest, GetPlacesToExploreResponse>(
  'getPlacesToExplore',
);

/** Files a housekeeping / laundry / restaurant request; the server prices it. */
export const createServiceRequest = callable<
  CreateServiceRequestRequest,
  CreateServiceRequestResponse
>('createServiceRequest');

/** Withdraws one of the guest's own requests while it is still REQUESTED. */
export const cancelServiceRequest = callable<CancelServiceRequestRequest, RequestStatusResponse>(
  'cancelServiceRequest',
);

export const submitFeedback = callable<SubmitFeedbackRequest, SubmitFeedbackResponse>(
  'submitFeedback',
);
